import { journeyStats } from './journeysData';

// Compatibility mapping: Laravel journey/shop records -> the shape
// JourneyCard/JourneyDetail were built against (journeysData.js's mock
// JOURNEYS). Isolated here so it's obvious which fields are real backend
// data and which are neutral placeholders standing in for data the backend
// doesn't provide yet.
//
// - title, subtitle: real (title, description) from Laravel.
// - theme: no Laravel equivalent. JourneyScene currently renders the same
//   artwork for every theme value, so a fixed default is a safe placeholder.
// - shop.category / shop.style: no Laravel equivalent (Shop has no category
//   field yet). Left blank/undefined; JourneyDetail already falls back to
//   CATEGORY_STYLES.cafe when style is missing.
// - shop.visited: derived from the authenticated user's own user_missions
//   (GET /user-missions, Step 5H) via isShopVisited below. `userMissions`
//   defaults to [] (not yet loaded / genuinely none) so every shop reads as
//   not-visited until real data says otherwise — never fabricated.
export function mapJourneyForUi(journey, shopsForJourney = [], userMissions = []) {
  return {
    id: journey.id,
    location_id: journey.location_id,
    title: journey.title,
    subtitle: journey.description || '',
    theme: 'mountain',
    places_required: journey.places_required,
    start_date: journey.start_date,
    expiry_date: journey.expiry_date,
    active: journey.active,
    created_at: journey.created_at,
    updated_at: journey.updated_at,
    shops: shopsForJourney.map((shop) => ({
      id: shop.id,
      name: shop.name,
      category: '',
      style: undefined,
      visited: isShopVisited(userMissions, shop.id),
    })),
  };
}

// A shop counts as visited only when the authenticated user's mission for it
// already has a recorded point transaction — the same rule Step 5F's
// points() endpoint uses server-side to decide a completed shop. current_step
// / status alone are never treated as proof, so this never shows a shop as
// done before Laravel actually confirms it earned its point.
export function isShopVisited(userMissions, shopId) {
  const mission = (userMissions || []).find((m) => m.shop_id === shopId);
  return Boolean(mission && Array.isArray(mission.point_transactions) && mission.point_transactions.length > 0);
}

// Builds My Profile's "Current Journey" / "Completed Journeys" entries
// directly from the authenticated user's own user_missions (GET
// /user-missions, Step 5H) — each mission already carries its journey via
// Laravel's eager-loaded `journey` relation, so this makes no separate
// journeys/shops API call. Progress is expressed against
// journeys.places_required (the same "stars" total Points/Journey already
// use), not the journey's full configured shop list, because user_missions
// only contains shops the user has actually started a mission for — never
// every shop configured for the journey.
export function summarizeJourneysFromMissions(userMissions) {
  const missions = userMissions || [];
  const byJourney = new Map();

  missions.forEach((mission) => {
    const journey = mission.journey;
    if (!journey || mission.journey_id == null) return;

    if (!byJourney.has(mission.journey_id)) {
      byJourney.set(mission.journey_id, { journey, missions: [] });
    }
    byJourney.get(mission.journey_id).missions.push(mission);
  });

  const journeys = Array.from(byJourney.values()).map(({ journey, missions: journeyMissions }) => {
    const completedShops = journeyMissions.filter(
      (m) => Array.isArray(m.point_transactions) && m.point_transactions.length > 0
    ).length;
    const placesRequired = journey.places_required || completedShops || 1;
    const total = Math.max(placesRequired, completedShops);

    return {
      id: journey.id,
      title: journey.title,
      subtitle: journey.description || '',
      theme: 'mountain',
      shops: Array.from({ length: total }, (_, i) => ({
        id: i + 1,
        visited: i < completedShops,
      })),
    };
  });

  const currentJourney = journeys.find((j) => journeyStats(j).pct < 100) || null;
  const completedJourneys = journeys.filter((j) => journeyStats(j).pct === 100);

  return { currentJourney, completedJourneys };
}
